<i18n>
{
  "de": {
    "getTickets": "Tickets",
    "soldOut": "Ausverkauft",
    "free": "Kostenlos",
    "from": "ab"
  },
  "en": {
    "getTickets": "Tickets",
    "soldOut": "Sold Out",
    "free": "Free",
    "from": "from"
  }
}
</i18n>

<template>
  <article class="event-card">
    <!-- Event Image -->
    <div class="event-card__image-wrapper">
      <img
        v-if="eventImage"
        :src="eventImage"
        :alt="event.title"
        class="event-card__image"
        loading="lazy"
      >
      <div
        v-else
        class="event-card__image event-card__image--placeholder"
      >
        <span class="event-card__placeholder-icon">🎫</span>
      </div>
    </div>

    <!-- Event Content -->
    <div class="event-card__content">
      <!-- Date & Location Row -->
      <div class="event-card__header">
        <div class="event-card__date">
          <span class="event-card__date-day">{{ eventDay }}</span>
          <span class="event-card__date-month">{{ eventMonth }}</span>
        </div>
        <div class="event-card__meta">
          <p
            v-if="eventLocation"
            class="event-card__location"
          >
            📍 {{ eventLocation }}
          </p>
          <p
            v-if="eventTime"
            class="event-card__time"
          >
            🕐 {{ eventTime }}
          </p>
        </div>
      </div>

      <!-- Event Info -->
      <div class="event-card__info">
        <h3 class="event-card__title">
          {{ event.title }}
        </h3>
      </div>

      <!-- Price & CTA -->
      <div class="event-card__footer">
        <div class="event-card__price">
          <template v-if="isFree">
            {{ $t('free') }}
          </template>
          <template v-else-if="hasMultiplePrices">
            {{ $t('from') }} {{ formattedLowestPrice }}
          </template>
          <template v-else>
            {{ formattedLowestPrice }}
          </template>
        </div>
        <a
          :href="ticketUrl"
          class="event-card__cta event-card__cta--on-product"
          :class="{ 'event-card__cta--disabled': isSoldOut }"
          @click.prevent="handleTicketClick"
        >
          {{ isSoldOut ? $t('soldOut') : $t('getTickets') }}
        </a>
      </div>
    </div>
  </article>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'EventCard',

  props: {
    event: {
      type: Object,
      required: true
    }
  },

  computed: {
    ...mapState(['artist']),

    // Build image URL from coverImage using media service pattern
    eventImage() {
      if (!this.event?.coverImage?.id || !this.artist?._id) return null
      // Use 600px width with auto height (0) to maintain aspect ratio
      return `/services/media/ticketier/image/${this.artist._id}/${this.event.coverImage.id}/600/0/image.webp`
    },

    // Parse startDate
    eventDate() {
      return this.event?.startDate ? new Date(this.event.startDate) : null
    },

    eventDay() {
      if (!this.eventDate) return '--'
      return this.eventDate.getDate()
    },

    eventMonth() {
      if (!this.eventDate) return '---'
      return this.eventDate.toLocaleString(this.$i18n.locale, { month: 'short' }).toUpperCase()
    },

    eventTime() {
      if (!this.eventDate) return ''
      return this.eventDate.toLocaleTimeString(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // Combine venue name and city for location display
    eventLocation() {
      const venue = this.event?.venue
      if (!venue) return null
      if (venue.name && venue.city) {
        return `${venue.name}, ${venue.city}`
      }
      return venue.name || venue.city || null
    },

    // Get active ticket categories
    activeCategories() {
      return this.event?.ticketCategories?.filter(c => c.active) || []
    },

    // Find the lowest price from active ticket categories
    lowestPrice() {
      if (!this.activeCategories.length) return null
      return Math.min(...this.activeCategories.map(c => c.priceInCents))
    },

    // Check if there are multiple different prices
    hasMultiplePrices() {
      if (this.activeCategories.length <= 1) return false
      const prices = new Set(this.activeCategories.map(c => c.priceInCents))
      return prices.size > 1
    },

    isFree() {
      return this.lowestPrice === null || this.lowestPrice === 0
    },

    // Check if all active categories are sold out
    isSoldOut() {
      if (!this.activeCategories.length) return true
      return this.activeCategories.every(c => c.sold >= c.quota)
    },

    formattedLowestPrice() {
      if (this.lowestPrice === null) return ''
      return this.centToCurrency(this.lowestPrice, 1, 'EUR')
    },

    // Link to events checkout page
    ticketUrl() {
      return `/events/${this.event.id}`
    }
  },

  methods: {
    handleTicketClick() {
      if (this.isSoldOut) return

      // Navigate to ticket page
      if (this.ticketUrl) {
        window.location.href = this.ticketUrl
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "assets/sass/_variables";

.event-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--product-background-color, #1e1b26);
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  // Image - square 1:1 aspect ratio with center-top alignment
  &__image-wrapper {
    position: relative;
    overflow: hidden;
    aspect-ratio: 1 / 1; // Square aspect ratio
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top; // Align image center horizontally, top vertically
    display: block;
    transition: transform 0.3s ease;
    // Fade mask matching subscription/product cards
    -webkit-mask-image: -webkit-gradient(
      linear,
      left top,
      left bottom,
      color-stop(0, rgba(0, 0, 0, 1)),
      color-stop(0.85, rgba(0, 0, 0, 1)),
      color-stop(0.95, rgba(0, 0, 0, 0.5)),
      color-stop(1, rgba(0, 0, 0, 0))
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 1) 85%,
      rgba(0, 0, 0, 0.5) 95%,
      rgba(0, 0, 0, 0) 100%
    );

    &--placeholder {
      background-color: var(--product-background-color, #1e1b26);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &:hover &__image {
    transform: scale(1.05);
  }

  &__placeholder-icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  // Content
  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 14px;
    background-color: var(--product-background-color, #1e1b26);
    container-type: inline-size; // Enables card-width container queries for the footer
  }

  // Header row with date and location/time
  &__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  // Date Badge
  &__date {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 54px;
    height: 54px;
    background: transparent;
    border: 1px solid var(--product-background-text-color, #fff);
    color: var(--product-background-text-color, #fff);
    border-radius: 8px;
    flex-shrink: 0;
  }

  // Meta info (location & time)
  &__meta {
    flex: 1;
    min-width: 0; // Allow text truncation
  }

  &__date-day {
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 1;
  }

  &__date-month {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 2px;
  }

  // Info
  &__info {
    flex: 1;
    margin-bottom: 14px;
  }

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--product-background-text-color, #fff);
    margin: 0 0 8px 0;
    line-height: 1.3;
    // Truncate to 2 lines
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__location,
  &__time {
    font-size: 0.85rem;
    color: var(--product-background-text-color, #ccc);
    margin: 0 0 2px 0;
    // Truncate long text
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__time {
    margin-bottom: 0; // Last item, no bottom margin
  }

  // Footer
  &__footer {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-wrap: wrap; // Fallback for browsers without container queries
    padding-top: 14px;
    margin-top: auto;

    // Theme-aware border using pseudo-element
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: var(--product-background-text-color, #fff);
      opacity: 0.15;
    }
  }

  &__price {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--product-background-text-color, #fff);
  }

  &__cta {
    display: block;
    padding: 8px 12px;
    background: var(--primary-color, #333);
    color: var(--primary-text-color, #fff);
    font-size: 0.8rem;
    font-weight: 600;
    border-radius: 6px;
    text-decoration: none;
    transition: all 0.2s ease;
    cursor: pointer;
    border: none;

    &--on-product {
      background: var(--product-background-text-color, #fff);
      color: var(--product-background-color, #1e1b26);
    }

    &:hover:not(&--disabled) {
      opacity: 0.9;
    }

    &--disabled {
      background: rgba(255, 255, 255, 0.1);
      color: var(--product-background-text-color, #999);
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

// Narrow cards (e.g. two-column mobile grid): stack price above a full-width button
@container (max-width: 239px) {
  .event-card__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .event-card__price {
    text-align: left;
  }

  .event-card__cta {
    width: 100%;
    text-align: center;
  }
}

// Mobile adjustments - keep fonts readable, don't shrink too much
@media screen and (max-width: #{$mobile}) {
  .event-card {
    &__content {
      padding: 12px;
    }

    &__date {
      width: 48px;
      height: 48px;
    }

    &__header {
      gap: 10px;
      margin-bottom: 12px;
    }

    &__date-day {
      font-size: 1.2rem;
    }

    &__date-month {
      font-size: 0.65rem;
    }

    &__title {
      font-size: 1rem; // Keep readable on mobile
      margin-bottom: 6px;
    }

    &__location,
    &__time {
      font-size: 0.8rem;
    }

    &__info {
      margin-bottom: 12px;
    }

    &__footer {
      padding-top: 12px;
    }

    &__price {
      font-size: 1.15rem;
    }

    &__cta {
      padding: 8px 14px; // Good touch target
      font-size: 0.8rem;
    }
  }
}
</style>
